/**
 * URL 파싱 유틸
 *
 * 현재 페이지 URL, 리퍼러, UTM 파라미터 추출
 */

/** UTM 파라미터 */
export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

/** 현재 페이지 URL 정보 추출 */
export function getPageLocation(): string {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

/** 페이지 제목 추출 */
export function getPageTitle(): string {
  if (typeof document === 'undefined') return '';
  return document.title;
}

/** 리퍼러 URL 추출 */
export function getPageReferrer(): string {
  if (typeof document === 'undefined') return '';
  return document.referrer;
}

/** UTM 파라미터 추출 */
export function getUtmParams(): UtmParams {
  if (typeof window === 'undefined') return {};

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const params: UtmParams = {};

    for (const key of UTM_KEYS) {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    }

    return params;
  } catch {
    return {};
  }
}

/**
 * 광고 매칭용 클릭 식별자 (FRD 38 §8.8).
 *
 * - fbclid: Meta(Facebook/Instagram) 광고 클릭 ID
 * - gclid:  Google Ads 클릭 ID
 * - wbraid / gbraid: iOS 14+ Google Ads 앱↔웹 측정 ID (개인정보 제약 환경)
 * - fbc / fbp: Meta CAPI 매칭용 브라우저 쿠키 값(_fbc / _fbp)
 * 모든 필드 optional — 미수집 시 생략.
 */
export interface ClickIds {
  fbclid?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  fbc?: string;
  fbp?: string;
}

const CLICK_ID_QUERY_KEYS = ['fbclid', 'gclid', 'wbraid', 'gbraid'] as const;

/**
 * 랜딩 URL 의 query string 에서 광고 클릭 식별자를 파싱한다.
 * 값이 있는 키만 포함된 부분 객체를 반환(없으면 빈 객체).
 */
export function getClickIdsFromUrl(): ClickIds {
  if (typeof window === 'undefined') return {};

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const ids: ClickIds = {};

    for (const key of CLICK_ID_QUERY_KEYS) {
      const value = searchParams.get(key);
      if (value) {
        ids[key] = value;
      }
    }

    return ids;
  } catch {
    return {};
  }
}

/**
 * 브라우저 쿠키에서 단일 값을 안전하게 조회(SSR-safe, 실패 시 undefined).
 * Meta 픽셀이 심는 _fbc / _fbp 캡처에 사용.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  try {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Meta 픽셀이 심은 _fbc / _fbp 쿠키를 캡처한다.
 * 값이 있는 키만 포함된 부분 객체를 반환(없으면 빈 객체).
 */
export function getFbCookies(): Pick<ClickIds, 'fbc' | 'fbp'> {
  const result: Pick<ClickIds, 'fbc' | 'fbp'> = {};
  const fbc = readCookie('_fbc');
  const fbp = readCookie('_fbp');
  if (fbc) result.fbc = fbc;
  if (fbp) result.fbp = fbp;
  return result;
}

/**
 * fbc 합성 — fbclid 로부터 Meta CAPI 규격(`fb.1.<creation_time>.<fbclid>`)을 만든다.
 * creation_time 세그먼트는 Meta 규격상 '밀리초' 단위 UNIX timestamp 이며,
 * 실제 픽셀이 심는 _fbc 쿠키와 동일한 단위다(초 변환 금지).
 *
 * @param fbclid   URL 에서 파싱한 fbclid
 * @param timestampMs  캡처 시각(ms, 그대로 사용). 기본값 Date.now().
 */
export function synthesizeFbc(fbclid: string, timestampMs: number = Date.now()): string {
  return `fb.1.${timestampMs}.${fbclid}`;
}
